import type { FC, ReactNode, RefObject, PointerEvent as ReactPointerEvent } from 'react'

import { useEffect, useRef, useState } from 'react'
import { gsap } from 'gsap'

import {
    AlignLeft,
    ArrowUp,
    Calendar,
    Check,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    ChevronsRight,
    Columns3,
    Copy,
    CornerUpLeft,
    Folder,
    Image as ImageIcon,
    LayoutGrid,
    List,
    Loader2,
    MapPin,
    Mic,
    Paperclip,
    Pin,
    Play,
    Plus,
    PlusCircle,
    RefreshCw,
    Search,
    Share,
    Smile,
    ThumbsDown,
    ThumbsUp,
    X as XIcon
} from 'lucide-react'

import type { AtelierProject } from '@/constants/atelier-data'

import { cn } from '@/lib/utils'
import { AppGlyph, VerifiedBadge } from '@/components/landing/atelier/icons'

import {
    DESIGNER,
    NOTES,
    PHOTOS,
    PROJECTS,
    VIDEO_EMBED,
    X_PROFILE,
    isVideoSrc
} from '@/constants/atelier-data'

export type WindowKind =
    | 'project'
    | 'notes'
    | 'photos'
    | 'claude'
    | 'finder'
    | 'video'
    | 'compose'
    | 'tweet'

export interface OpenWindow {
    uid: number
    kind: WindowKind
    z: number
    x: number
    y: number
    w: number
    h: number
    title: string
    projectId?: string
    folderName?: string
}

export const DEFAULT_WINDOW_SIZES: Record<WindowKind, { w: number; h: number }> = {
    project: { w: 980, h: 620 },
    notes: { w: 720, h: 520 },
    photos: { w: 880, h: 600 },
    claude: { w: 720, h: 620 },
    finder: { w: 760, h: 460 },
    video: { w: 720, h: 460 },
    compose: { w: 640, h: 580 },
    tweet: { w: 600, h: 640 }
}

export interface WindowCtx {
    isMobile: boolean
    openProject: (projectId: string) => void
    openVideo: (title: string) => void
    openLightbox: (src: string) => void
    closeWindow: (uid: number) => void
}

interface FrameProps {
    win: OpenWindow
    isMobile: boolean
    sectionRef: RefObject<HTMLElement | null>
    onClose: (uid: number) => void
    onFocus: (uid: number) => void
    onMove: (uid: number, x: number, y: number) => void
    title?: string
    headerCenter?: ReactNode
    headerRight?: ReactNode
    headerClassName?: string
    bodyClassName?: string
    children: ReactNode
}

const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max)

export const WindowFrame: FC<FrameProps> = ({
    win,
    isMobile,
    sectionRef,
    onClose,
    onFocus,
    onMove,
    title,
    headerCenter,
    headerRight,
    headerClassName,
    bodyClassName,
    children
}): ReactNode => {
    const frameRef = useRef<HTMLDivElement>(null)
    const drag = useRef({ startX: 0, startY: 0, originX: 0, originY: 0, moved: false, pointerId: -1, dragging: false })

    useEffect(() => {
        const el = frameRef.current
        if (!el) return

        gsap.fromTo(
            el,
            { opacity: 0, scale: 0.94, y: 14, filter: 'blur(6px)' },
            { opacity: 1, scale: 1, y: 0, filter: 'blur(0px)', duration: 0.36, ease: 'power3.out' }
        )
    }, [])

    const onHeaderPointerDown = (e: ReactPointerEvent<HTMLDivElement>): void => {
        if (isMobile) return
        if (e.button !== 0) return
        const target = e.target as HTMLElement
        if (target.closest('[data-traffic-light]')) return

        const el = frameRef.current
        if (!el) return

        onFocus(win.uid)
        el.setPointerCapture(e.pointerId)
        drag.current = {
            startX: e.clientX,
            startY: e.clientY,
            originX: win.x,
            originY: win.y,
            moved: false,
            pointerId: e.pointerId,
            dragging: true
        }
    }

    const onHeaderPointerMove = (e: ReactPointerEvent<HTMLDivElement>): void => {
        const state = drag.current
        if (!state.dragging) return
        const el = frameRef.current
        const section = sectionRef.current
        if (!el || !section) return

        const dx = e.clientX - state.startX
        const dy = e.clientY - state.startY
        if (!state.moved && Math.hypot(dx, dy) < 3) return
        state.moved = true

        const sectionW = section.clientWidth
        const sectionH = section.clientHeight
        const nx = clamp(state.originX + dx, -win.w + 80, sectionW - 80)
        const ny = clamp(state.originY + dy, 28, sectionH - 60)
        el.style.left = `${nx}px`
        el.style.top = `${ny}px`
    }

    const onHeaderPointerUp = (e: ReactPointerEvent<HTMLDivElement>): void => {
        const state = drag.current
        if (!state.dragging) return
        const el = frameRef.current
        if (el && el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId)
        state.dragging = false

        if (state.moved && el) {
            const left = parseFloat(el.style.left || `${win.x}`)
            const top = parseFloat(el.style.top || `${win.y}`)
            onMove(win.uid, left, top)
        }
    }

    return (
        <div
            ref={frameRef}
            role='dialog'
            aria-label={title || win.kind}
            onMouseDown={() => onFocus(win.uid)}
            className='pointer-events-auto absolute flex flex-col overflow-hidden rounded-[12px] border border-black/15 bg-[#fbfaf7] text-[#1a1612] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.45),0_8px_24px_-8px_rgba(0,0,0,0.35)]'
            style={{ left: win.x, top: win.y, width: win.w, height: win.h, zIndex: win.z }}
        >
            <div
                onPointerDown={onHeaderPointerDown}
                onPointerMove={onHeaderPointerMove}
                onPointerUp={onHeaderPointerUp}
                className={cn(
                    'flex shrink-0 items-center gap-3 border-b border-black/[0.07] px-3 py-2 select-none',
                    isMobile ? 'cursor-default' : 'cursor-grab active:cursor-grabbing',
                    headerClassName || 'bg-[#ecebe6]'
                )}
                style={{ touchAction: 'none' }}
            >
                <div data-traffic-light className='flex items-center gap-1.5'>
                    <button
                        type='button'
                        aria-label='Close window'
                        onClick={() => onClose(win.uid)}
                        className='group/tl flex size-3 items-center justify-center rounded-full bg-[#ff5f57] ring-1 ring-black/10'
                    >
                        <svg width='6' height='6' viewBox='0 0 6 6' className='opacity-0 group-hover/tl:opacity-100'>
                            <path d='M1 1 L5 5 M5 1 L1 5' stroke='#5a0900' strokeWidth='1.1' strokeLinecap='round' />
                        </svg>
                    </button>
                    <span className='size-3 rounded-full bg-[#febc2e] ring-1 ring-black/10' aria-hidden='true' />
                    <span className='size-3 rounded-full bg-[#28c840] ring-1 ring-black/10' aria-hidden='true' />
                </div>

                <div className='flex min-w-0 flex-1 items-center justify-center'>
                    {headerCenter ?? (
                        <span className='truncate text-[12px] font-medium tracking-tight text-black/70'>{title}</span>
                    )}
                </div>

                <div className='flex shrink-0 items-center justify-end' style={{ minWidth: 60 }}>
                    {headerRight}
                </div>
            </div>

            <div className={cn('relative min-h-0 flex-1 overflow-hidden', bodyClassName)}>{children}</div>
        </div>
    )
}

/* ---------------------------------- Safari toolbar --------------------------------- */

const SafariPillButton: FC<{ children: ReactNode; label: string; compact?: boolean; disabled?: boolean }> = ({
    children,
    label,
    compact,
    disabled
}): ReactNode => (
    <button
        type='button'
        aria-label={label}
        disabled={disabled}
        className={cn(
            'rounded-full text-black/60 transition-colors hover:bg-black/[0.06] hover:text-black/80',
            compact ? 'px-1 py-1' : 'px-2 py-1',
            disabled && 'text-black/25 hover:bg-transparent hover:text-black/25'
        )}
    >
        {children}
    </button>
)

const SafariPill: FC<{ children: ReactNode; className?: string }> = ({ children, className }): ReactNode => (
    <div
        className={cn(
            'flex h-[30px] items-center rounded-full bg-white px-1 shadow-[inset_0_0_0_1px_rgba(0,0,0,0.05),0_1px_0_rgba(255,255,255,0.6)]',
            className
        )}
    >
        {children}
    </div>
)

export const SafariToolbar: FC<{ url: string }> = ({ url }): ReactNode => (
    <div className='flex shrink-0 items-center gap-2 border-b border-black/[0.08] bg-[#ebebeb] px-3 py-2'>
        <SafariPill className='hidden md:flex'>
            <SafariPillButton label='Sidebar'>
                <svg width='15' height='13' viewBox='0 0 15 13' fill='none' stroke='currentColor' strokeWidth='1.4'>
                    <rect x='0.7' y='0.7' width='13.6' height='11.6' rx='2' />
                    <line x1='5' y1='0.7' x2='5' y2='12.3' />
                </svg>
            </SafariPillButton>
            <SafariPillButton label='Sidebar options' compact>
                <ChevronDown className='size-3' />
            </SafariPillButton>
        </SafariPill>

        <div className='hidden items-center px-1 text-black/55 md:flex'>
            <SafariPillButton label='Back'>
                <ChevronLeft className='size-3.5' />
            </SafariPillButton>
            <span className='h-3.5 w-px bg-black/15' />
            <SafariPillButton label='Forward' disabled>
                <ChevronRight className='size-3.5' />
            </SafariPillButton>
        </div>

        <div className='flex h-[30px] min-w-0 flex-1 items-center gap-2 rounded-full bg-white px-3 shadow-[inset_0_0_0_1px_rgba(0,0,0,0.05),0_1px_0_rgba(255,255,255,0.6)]'>
            <AlignLeft className='size-[15px] shrink-0 text-black/45' />
            <span className='min-w-0 flex-1 truncate text-center text-[12.5px] text-[#1a1612]'>{url}</span>
            <RefreshCw className='size-3.5 shrink-0 text-black/45' />
        </div>

        <SafariPill className='hidden md:flex'>
            <SafariPillButton label='Share'>
                <Share className='size-3.5' />
            </SafariPillButton>
            <SafariPillButton label='New tab'>
                <Plus className='size-3.5' />
            </SafariPillButton>
        </SafariPill>

        <SafariPill className='hidden md:flex'>
            <SafariPillButton label='Show all tabs'>
                <ChevronsRight className='size-3.5' />
            </SafariPillButton>
        </SafariPill>
    </div>
)

/* ---------------------------------- Project body --------------------------------- */

const BriefBlock: FC<{ label: string; body: string }> = ({ label, body }): ReactNode => (
    <div>
        <p className='text-[10px] font-semibold uppercase tracking-[0.12em] text-black/45'>{label}</p>
        <p className='mt-1.5 text-[13px] leading-relaxed text-black/75'>{body}</p>
    </div>
)

const GalleryVideoThumb: FC<{ src: string; label: string }> = ({ src, label }): ReactNode => {
    const videoRef = useRef<HTMLVideoElement>(null)

    return (
        <>
            <video
                ref={videoRef}
                src={`${src}#t=0.1`}
                aria-label={label}
                muted
                loop
                playsInline
                preload='metadata'
                onMouseEnter={() => {
                    videoRef.current?.play().catch(() => undefined)
                }}
                onMouseLeave={() => {
                    videoRef.current?.pause()
                }}
                className='size-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]'
            />
            <span className='pointer-events-none absolute bottom-2 left-2 flex size-7 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-sm'>
                <Play className='size-3.5 fill-current' />
            </span>
        </>
    )
}

const ProjectBody: FC<{ project: AtelierProject; ctx: WindowCtx }> = ({ project, ctx }): ReactNode => {
    const labels = project.sectionLabels
    const longBrief = Object.values(project.brief).some((text) => text.length > 180)

    return (
        <div className='h-full overflow-y-auto overflow-x-hidden px-5 py-6 sm:px-9 sm:py-8'>
            <div className='grid grid-cols-12 gap-5 border-b border-black/[0.08] pb-6'>
                <div className='col-span-12 md:col-span-2'>
                    <p className='text-[10px] font-semibold uppercase tracking-[0.18em] text-black/45'>
                        {project.eyebrow ?? '01 — Project'}
                    </p>
                    <p className='mt-1 text-[10px] uppercase tracking-[0.18em] text-black/45'>{project.category}</p>
                </div>
                <div className='col-span-12 md:col-span-10'>
                    <h2 className='text-[32px] font-semibold leading-[1.02] tracking-[-0.025em] text-[#1a1612] sm:text-[42px]'>
                        {project.title}
                    </h2>
                    <p className='mt-3 max-w-2xl text-[14px] leading-snug text-black/65'>{project.positioning}</p>
                </div>
            </div>

            <div className='grid grid-cols-12 gap-5 border-b border-black/[0.08] py-7 sm:gap-8'>
                <div className='col-span-12 md:col-span-5'>
                    <p className='text-[10px] font-semibold uppercase tracking-[0.18em] text-black/45'>
                        {labels?.overview ?? 'Overview'}
                    </p>
                    <p className='mt-2 text-[13.5px] leading-relaxed text-black/80'>{project.description}</p>
                </div>
                <div className='col-span-12 md:col-span-7'>
                    {!labels && (
                        <p className='mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-black/45'>Brief</p>
                    )}
                    <div className={cn('grid gap-5', longBrief ? 'grid-cols-1 gap-6' : 'sm:grid-cols-3')}>
                        <BriefBlock label={labels?.challenge ?? 'Challenge'} body={project.brief.challenge} />
                        <BriefBlock label={labels?.direction ?? 'Direction'} body={project.brief.direction} />
                        <BriefBlock label={labels?.outcome ?? 'Outcome'} body={project.brief.outcome} />
                    </div>
                </div>
            </div>

            <div className='py-7'>
                <p className='text-[10px] font-semibold uppercase tracking-[0.18em] text-black/45'>Gallery</p>
                <div className='mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3'>
                    {project.gallerySrcs.slice(0, 9).map((src, i) => {
                        const label = `${project.title} gallery ${isVideoSrc(src) ? 'video' : 'image'} ${i + 1}`

                        return (
                            <button
                                key={`${src}-${i}`}
                                type='button'
                                aria-label={`Open ${label}`}
                                onClick={() => ctx.openLightbox(src)}
                                className='group relative aspect-square overflow-hidden rounded-[6px] bg-black/5'
                            >
                                {isVideoSrc(src) ? (
                                    <GalleryVideoThumb src={src} label={label} />
                                ) : (
                                    <img
                                        src={src}
                                        alt={label}
                                        draggable={false}
                                        loading='lazy'
                                        className='size-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]'
                                    />
                                )}
                            </button>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}

/* ---------------------------------- Notes body --------------------------------- */

const NotesBody: FC<{ ctx: WindowCtx }> = ({ ctx }): ReactNode => {
    const [activeId, setActiveId] = useState(NOTES[0].id)
    const [mobileView, setMobileView] = useState<'list' | 'detail'>('list')
    const active = NOTES.find((n) => n.id === activeId) ?? NOTES[0]

    const showList = !ctx.isMobile || mobileView === 'list'
    const showDetail = !ctx.isMobile || mobileView === 'detail'

    return (
        <div className='flex h-full'>
            {showList && (
                <div className={cn('flex flex-col bg-[#f7f5ee] md:border-r md:border-black/[0.08]', ctx.isMobile ? 'w-full' : 'w-60')}>
                    <p className='border-b border-black/[0.06] px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-black/45'>
                        Notes — {NOTES.length}
                    </p>
                    <div className='flex-1 overflow-y-auto'>
                        {NOTES.map((note) => (
                            <button
                                key={note.id}
                                type='button'
                                onClick={() => {
                                    setActiveId(note.id)
                                    if (ctx.isMobile) setMobileView('detail')
                                }}
                                className={cn(
                                    'block w-full border-b border-black/[0.05] px-4 py-3 text-left transition-colors',
                                    note.id === activeId && !ctx.isMobile ? 'bg-[#f4d34c]/60' : 'hover:bg-black/[0.03]'
                                )}
                            >
                                <p className='truncate text-[13px] font-semibold text-[#1a1612]'>{note.title}</p>
                                <div className='mt-0.5 flex items-baseline gap-2'>
                                    <span className='text-[11px] font-medium text-black/70'>{note.date}</span>
                                    <span className='truncate text-[11px] text-black/55'>{note.preview}</span>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {showDetail && (
                <div className='flex min-w-0 flex-1 flex-col overflow-hidden'>
                    {ctx.isMobile && (
                        <button
                            type='button'
                            onClick={() => setMobileView('list')}
                            className='flex items-center gap-1 border-b border-black/[0.06] px-3 py-2.5 text-[13px] font-medium text-[#1a1612]'
                        >
                            <ChevronLeft className='size-4' />
                            Notes
                        </button>
                    )}
                    <div className='flex-1 overflow-y-auto px-5 py-6 sm:px-8 sm:py-7'>
                        <p className='text-[11px] text-black/45'>{active.date}</p>
                        <h3 className='mt-1 text-[22px] font-semibold leading-tight tracking-tight text-[#1a1612]'>
                            {active.title}
                        </h3>
                        <p className='mt-5 whitespace-pre-line text-[14px] leading-relaxed text-black/80'>{active.body}</p>
                    </div>
                </div>
            )}
        </div>
    )
}

/* ---------------------------------- Photos body --------------------------------- */

const PhotosBody: FC<{ ctx: WindowCtx }> = ({ ctx }): ReactNode => (
    <div className='h-full overflow-y-auto px-6 py-7 sm:px-8 sm:py-8'>
        <p className='text-[10px] font-semibold uppercase tracking-[0.18em] text-black/45'>Recents · {PHOTOS.length}</p>
        <div className='mt-4 grid auto-rows-[120px] grid-cols-4 gap-3' style={{ gridAutoFlow: 'row dense' }}>
            {PHOTOS.map((photo, i) => (
                <button
                    key={photo.src}
                    type='button'
                    onClick={() => ctx.openLightbox(photo.src)}
                    className={cn(
                        'group relative overflow-hidden rounded-[8px] bg-black/5',
                        photo.ratio === 'tall' && 'row-span-2',
                        photo.ratio === 'wide' && 'col-span-2'
                    )}
                >
                    <img
                        src={photo.src}
                        alt={photo.caption}
                        loading='lazy'
                        draggable={false}
                        className='size-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.05]'
                    />
                    <span className='pointer-events-none absolute inset-x-0 bottom-0 translate-y-2 bg-gradient-to-t from-black/70 to-transparent px-3 py-2 text-[11px] font-medium text-white opacity-0 transition-all duration-200 group-hover:translate-y-0 group-hover:opacity-100'>
                        {photo.caption}
                    </span>
                    {i === 0 && <span className='sr-only'>Open photo</span>}
                </button>
            ))}
        </div>
    </div>
)

/* ---------------------------------- Claude body --------------------------------- */

const claudeRecents = [
    'Stop explosive diarrhea?',
    'Delete embarrassing video',
    'How to make it pop',
    'Can you make this better?',
    'How to be famous on X',
    'Find me a job — $1M a year'
]

const ClaudeBody: FC<{ ctx: WindowCtx }> = ({ ctx }): ReactNode => {
    const [mobileView, setMobileView] = useState<'list' | 'detail'>('list')
    const youRef = useRef<HTMLDivElement>(null)
    const preambleRef = useRef<HTMLDivElement>(null)
    const replyRef = useRef<HTMLDivElement>(null)
    const actionsRef = useRef<HTMLDivElement>(null)

    const showList = !ctx.isMobile || mobileView === 'list'
    const showChat = !ctx.isMobile || mobileView === 'detail'

    useEffect(() => {
        if (ctx.isMobile && mobileView !== 'detail') return
        const tl = gsap.timeline({ defaults: { ease: 'power3.out' } })
        if (youRef.current) tl.fromTo(youRef.current, { opacity: 0, y: 6 }, { opacity: 1, y: 0, duration: 0.3 }, 0)
        if (preambleRef.current) tl.fromTo(preambleRef.current, { opacity: 0, y: 4 }, { opacity: 1, y: 0, duration: 0.25 }, 0.45)
        if (replyRef.current) tl.fromTo(replyRef.current, { opacity: 0, y: 4 }, { opacity: 1, y: 0, duration: 0.35 }, 0.75)
        if (actionsRef.current) tl.fromTo(actionsRef.current, { opacity: 0 }, { opacity: 1, duration: 0.25 }, 1.05)

        return () => {
            tl.kill()
        }
    }, [ctx.isMobile, mobileView])

    return (
        <div className='flex h-full bg-[#faf9f5] text-[#1f1d1a]'>
            {showList && (
                <div className={cn('flex flex-col bg-[#f1efe8] md:border-r md:border-black/[0.06]', ctx.isMobile ? 'w-full' : 'w-[220px]')}>
                    <div className='px-3 pt-4'>
                        <button
                            type='button'
                            className='flex w-full items-center gap-2 rounded-[10px] border border-black/[0.07] bg-white px-3 py-2 text-[13px] font-medium text-[#1f1d1a] shadow-[0_1px_0_rgba(0,0,0,0.03)] transition-colors hover:bg-black/[0.03]'
                        >
                            <Plus className='size-3.5' />
                            New chat
                        </button>
                    </div>
                    <p className='mt-5 px-4 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-black/45'>Recents</p>
                    <div className='mt-1 flex-1 overflow-y-auto px-2 pb-3'>
                        <button
                            type='button'
                            onClick={() => ctx.isMobile && setMobileView('detail')}
                            className='block w-full truncate rounded-[8px] bg-black/[0.08] px-2.5 py-1.5 text-left text-[13px] font-medium text-[#1f1d1a]'
                        >
                            Make me a cool portfolio
                        </button>
                        {claudeRecents.map((item) => (
                            <span
                                key={item}
                                className='mt-0.5 block w-full cursor-default truncate rounded-[8px] px-2.5 py-1.5 text-[13px] text-black/65 transition-colors hover:bg-black/[0.04]'
                            >
                                {item}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {showChat && (
                <div className='flex min-w-0 flex-1 flex-col overflow-hidden'>
                    {ctx.isMobile && (
                        <button
                            type='button'
                            onClick={() => setMobileView('list')}
                            className='flex items-center gap-1 border-b border-black/[0.06] px-3 py-2.5 text-[13px] font-medium text-[#1f1d1a]'
                        >
                            <ChevronLeft className='size-4' />
                            Claude
                        </button>
                    )}
                    <div className='flex-1 overflow-y-auto px-6 py-7 sm:px-8 sm:py-9'>
                        <div ref={youRef} className='flex justify-end'>
                            <p
                                className='max-w-[80%] rounded-[18px] bg-[#ecebe6] px-4 py-2 text-[15px]'
                                style={{ fontFamily: 'Charter, "Tiempos Text", ui-serif, Georgia, serif' }}
                            >
                                Make me a cool portfolio
                            </p>
                        </div>

                        <div className='mt-8'>
                            <div ref={preambleRef} className='flex items-center gap-1 text-[13.5px] text-black/45'>
                                Architected an animated portfolio showcasing a designer&apos;s best work
                                <ChevronRight className='size-[11px]' />
                            </div>
                            <p
                                ref={replyRef}
                                className='mt-3 text-[17px] leading-[1.5] text-[#1f1d1a]'
                                style={{ fontFamily: 'Charter, "Tiempos Text", ui-serif, Georgia, serif' }}
                            >
                                Aight, bet! Check{' '}
                                <a
                                    href='https://www.motionin.design'
                                    target='_blank'
                                    rel='noreferrer'
                                    className='underline decoration-black/30 underline-offset-2 transition-colors hover:text-[#c96442] hover:decoration-current'
                                >
                                    https://motionin.design/gallery/atelier
                                </a>
                            </p>

                            <div ref={actionsRef} className='mt-4 flex items-center gap-1 text-black/40'>
                                {[Copy, ThumbsUp, ThumbsDown, RefreshCw].map((Icon, i) => (
                                    <button
                                        key={i}
                                        type='button'
                                        aria-label='Message action'
                                        className='rounded-md p-1.5 transition-colors hover:bg-black/[0.05] hover:text-black/70'
                                    >
                                        <Icon className='size-4' />
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className='px-6 pb-4 pt-2'>
                        <div className='rounded-[22px] border border-black/[0.07] bg-white px-4 py-3 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.07)]'>
                            <p className='text-[15px] text-black/30'>Write a message…</p>
                            <div className='mt-3 flex items-center justify-between'>
                                <button type='button' aria-label='Attach' className='rounded-md p-1 transition-colors hover:bg-black/[0.05]'>
                                    <Plus className='size-4 text-black/55' />
                                </button>
                                <div className='flex items-center gap-2'>
                                    <span className='flex items-center gap-1 rounded-full border border-[#467cc6] px-3 py-[3px] text-[12px] text-[#1f1d1a]'>
                                        <span className='font-medium'>Haiku 4.5</span>
                                        <span className='text-black/45'>Extended</span>
                                        <ChevronDown className='size-3' />
                                    </span>
                                    <button type='button' aria-label='Voice' className='rounded-md p-1 transition-colors hover:bg-black/[0.05]'>
                                        <Mic className='size-4 text-black/55' />
                                    </button>
                                </div>
                            </div>
                        </div>
                        <p className='mt-2 text-center text-[11px] text-black/40'>
                            Claude is AI and can make mistakes. Please double-check responses.
                        </p>
                    </div>
                </div>
            )}
        </div>
    )
}

/* ---------------------------------- Finder body --------------------------------- */

const finderFavorites = [
    { label: 'Desktop', color: '#62a8e8' },
    { label: 'Applications', color: '#7c8595' },
    { label: 'Projects', color: '#7c8595' },
    { label: 'MotionIn.Design', color: '#5fd0c9' },
    { label: 'Work', color: '#a8a08b' },
    { label: 'Movies', color: '#d36e62' },
    { label: 'Downloads', color: '#7c8595' },
    { label: 'Documents', color: '#7c8595' }
]

const TRASH_PHOTOS = [
    { src: '/atelier/trash/3d-village.jpg', name: '3d village' },
    { src: '/atelier/trash/find-a-nature.jpg', name: 'find a nature' },
    { src: '/atelier/trash/mountain.jpg', name: 'mountain' },
    { src: '/atelier/trash/travel-journey.jpg', name: 'travel journey' },
    { src: '/atelier/trash/t-shirt-design.jpg', name: 't shirt design' },
    { src: '/atelier/trash/nothing.jpg', name: 'nothing' },
    { src: '/atelier/trash/cycling.jpg', name: 'cycling' },
    { src: '/atelier/trash/poster-skull.jpg', name: 'poster skull' },
    { src: '/atelier/trash/maybe-i-right.jpg', name: 'maybe i right' },
    { src: '/atelier/trash/meme.jpg', name: 'meme' },
    { src: '/atelier/trash/coffee-time.jpg', name: 'coffee time' },
    { src: '/atelier/trash/mah-cover-poster.jpg', name: 'mah cover poster' },
    { src: '/atelier/trash/me-and-my-cute-pet.jpg', name: 'me and my cute pet' }
]

const TRASH_LINKS = [
    { href: 'https://www.facebook.com/share/r/1Bz2w5rF4p/', name: 'in another life this may have been different.mov' }
]

const FinderFolderDot: FC<{ size?: number }> = ({ size = 14 }): ReactNode => (
    <Folder className='shrink-0 text-[#4aa3f0]' style={{ width: size, height: size }} fill='#4aa3f0' />
)

const ViewModeButton: FC<{ children: ReactNode; active?: boolean; label: string }> = ({ children, active, label }): ReactNode => (
    <button
        type='button'
        aria-label={label}
        className={cn('rounded p-1 text-black/45', active && 'bg-white text-black/70 shadow-[0_1px_0_rgba(0,0,0,0.08)]')}
    >
        {children}
    </button>
)

const FinderBody: FC<{ win: OpenWindow; ctx: WindowCtx }> = ({ win, ctx }): ReactNode => {
    const folderName = win.folderName ?? 'Folder'
    const fileName = folderName === 'Trash' ? "I hope my mom doesn't see this.mov" : 'Dont play this video.mov'

    return (
        <div className='flex h-full flex-col bg-[#f7f5ef]'>
            <div className='flex items-center gap-3 border-b border-black/[0.06] bg-[#ecebe6] px-3 py-1.5'>
                <div className='flex items-center text-black/35'>
                    <button type='button' aria-label='Back' className='rounded-md p-1 transition-colors hover:bg-black/5'>
                        <ChevronLeft className='size-3.5' />
                    </button>
                    <button type='button' aria-label='Forward' className='rounded-md p-1 transition-colors hover:bg-black/5'>
                        <ChevronRight className='size-3.5' />
                    </button>
                </div>
                <div className='flex items-center gap-1.5 text-[12px] font-semibold text-[#1a1612]'>
                    <FinderFolderDot />
                    {folderName}
                </div>
                <div className='flex-1' />
                <div className='flex items-center gap-0.5 rounded-md border border-black/10 bg-white/70 p-0.5'>
                    <ViewModeButton label='Icon view' active>
                        <LayoutGrid className='size-3.5' />
                    </ViewModeButton>
                    <ViewModeButton label='List view'>
                        <List className='size-3.5' />
                    </ViewModeButton>
                    <ViewModeButton label='Column view'>
                        <Columns3 className='size-3.5' />
                    </ViewModeButton>
                    <ViewModeButton label='Gallery view'>
                        <ImageIcon className='size-3.5' />
                    </ViewModeButton>
                </div>
                <button type='button' aria-label='Search' className='rounded-md p-1 text-black/40 transition-colors hover:bg-black/5'>
                    <Search className='size-3.5' />
                </button>
            </div>

            <div className='flex min-h-0 flex-1'>
                <div className='hidden w-40 shrink-0 overflow-y-auto bg-[#ecebe6] px-2 py-2 sm:block'>
                    <p className='px-1.5 py-1 text-[10px] font-semibold uppercase tracking-[0.06em] text-black/45'>iCloud</p>
                    {['Recents', 'Shared'].map((item) => (
                        <div key={item} className='flex items-center gap-2 rounded-[6px] px-1.5 py-1 transition-colors hover:bg-black/[0.06]'>
                            <span className='size-3 rounded-[3px]' style={{ background: '#7c8595' }} />
                            <span className='truncate text-[12px] text-[#1a1612]'>{item}</span>
                        </div>
                    ))}
                    <p className='mt-2 px-1.5 py-1 text-[10px] font-semibold uppercase tracking-[0.06em] text-black/45'>Favorites</p>
                    {finderFavorites.map((item) => (
                        <div key={item.label} className='flex items-center gap-2 rounded-[6px] px-1.5 py-1 transition-colors hover:bg-black/[0.06]'>
                            <span className='size-3 rounded-[3px]' style={{ background: item.color }} />
                            <span className='truncate text-[12px] text-[#1a1612]'>{item.label}</span>
                        </div>
                    ))}
                </div>

                <div className='min-w-0 flex-1 overflow-y-auto bg-white px-5 py-4'>
                    <div className='grid gap-x-2 gap-y-4' style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))' }}>
                        {folderName !== 'Trash' && (
                            <button
                                type='button'
                                onClick={() => ctx.openVideo(fileName)}
                                className='group flex flex-col items-center gap-1.5 rounded-md p-2 focus:outline-none focus-visible:bg-[#cce3ff]'
                            >
                                <span className='size-[68px]'>
                                    <AppGlyph id='mov' />
                                </span>
                                <span className='line-clamp-2 max-w-[96px] rounded-[4px] px-1 text-center text-[11px] font-medium leading-[1.2] text-[#1a1612] group-focus-visible:bg-[#3b82f6] group-focus-visible:text-white'>
                                    {fileName}
                                </span>
                            </button>
                        )}
                        {folderName === 'Trash' &&
                            TRASH_PHOTOS.map((photo) => (
                                <button
                                    key={photo.src}
                                    type='button'
                                    onClick={() => ctx.openLightbox(photo.src)}
                                    className='group flex flex-col items-center gap-1.5 rounded-md p-2 focus:outline-none focus-visible:bg-[#cce3ff]'
                                >
                                    <span className='size-[68px] overflow-hidden rounded-[6px] border border-black/10 shadow-sm'>
                                        <img src={photo.src} alt={photo.name} draggable={false} className='size-full object-cover' />
                                    </span>
                                    <span className='line-clamp-2 max-w-[96px] rounded-[4px] px-1 text-center text-[11px] font-medium leading-[1.2] text-[#1a1612] group-focus-visible:bg-[#3b82f6] group-focus-visible:text-white'>
                                        {photo.name}
                                    </span>
                                </button>
                            ))}
                        {folderName === 'Trash' &&
                            TRASH_LINKS.map((link) => (
                                <a
                                    key={link.href}
                                    href={link.href}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='group flex flex-col items-center gap-1.5 rounded-md p-2 focus:outline-none focus-visible:bg-[#cce3ff]'
                                >
                                    <span className='size-[68px]'>
                                        <AppGlyph id='mov' />
                                    </span>
                                    <span className='line-clamp-2 max-w-[96px] rounded-[4px] px-1 text-center text-[11px] font-medium leading-[1.2] text-[#1a1612] group-focus-visible:bg-[#3b82f6] group-focus-visible:text-white'>
                                        {link.name}
                                    </span>
                                </a>
                            ))}
                    </div>
                </div>
            </div>

            <div className='flex items-center gap-1.5 border-t border-black/[0.06] bg-[#f4f2eb] px-3 py-1.5 text-[10.5px] text-black/55'>
                <FinderFolderDot size={12} />
                <span className='flex items-center gap-1'>
                    Macintosh HD
                    <ChevronRight className='size-2.5 text-black/35' />
                    Users
                    <ChevronRight className='size-2.5 text-black/35' />
                    alex
                    <ChevronRight className='size-2.5 text-black/35' />
                    Desktop
                    <ChevronRight className='size-2.5 text-black/35' />
                    <span className='font-semibold text-black/75'>{folderName}</span>
                </span>
                <span className='ml-auto'>{folderName === 'Trash' ? `${TRASH_PHOTOS.length + TRASH_LINKS.length} items` : '1 item'}</span>
            </div>
        </div>
    )
}

/* ---------------------------------- Video body --------------------------------- */

const VideoBody: FC = (): ReactNode => (
    <div className='size-full bg-black'>
        <iframe
            src={VIDEO_EMBED}
            title='Video'
            className='size-full border-0'
            allow='autoplay; encrypted-media; picture-in-picture'
            allowFullScreen
        />
    </div>
)

/* ---------------------------------- Tweet body --------------------------------- */

// X action icons + verified badge are the supplied /atelier/x_ui SVGs, tinted per spec §17.
const X_ACTION_FILTER = 'invert(72%) sepia(0%) saturate(0%) brightness(95%)'

const tweetMetrics = [
    { src: '/atelier/x_ui/reaply.svg', label: '3.4K' },
    { src: '/atelier/x_ui/repost.svg', label: '12K' },
    { src: '/atelier/x_ui/like.svg', label: '89K' },
    { src: '/atelier/x_ui/reach.svg', label: '1.2M' },
    { src: '/atelier/x_ui/save.svg', label: '1.1K' },
    { src: '/atelier/x_ui/share.svg', label: '' }
]

const TweetBody: FC = (): ReactNode => (
    <div className='h-full overflow-y-auto bg-black text-white'>
        <SafariToolbar url='x.com/elonmusk' />

        <div className='sticky top-0 z-10 flex items-center gap-5 border-b border-white/10 bg-black/85 px-4 py-3 backdrop-blur-md'>
            <button type='button' aria-label='Back' className='rounded-full p-1.5 transition-colors hover:bg-white/10'>
                <ChevronLeft className='size-[18px]' />
            </button>
            <div className='flex flex-col'>
                <span className='text-[15px] font-bold leading-tight'>{X_PROFILE.name}</span>
                <span className='text-[12.5px] text-white/55'>{X_PROFILE.posts}</span>
            </div>
        </div>

        <div
            className='h-[150px] bg-cover bg-center'
            style={{ backgroundImage: `url(${X_PROFILE.cover})` }}
            aria-hidden='true'
        />

        <div className='relative px-4'>
            <div className='absolute -top-[56px] left-4 size-28 overflow-hidden rounded-full border-4 border-black bg-black'>
                <img src={X_PROFILE.avatar} alt={X_PROFILE.name} className='size-full object-cover' draggable={false} />
            </div>
            <div className='flex justify-end pt-3'>
                <button type='button' className='rounded-full bg-white px-4 py-1 text-[13.5px] font-bold text-black transition-colors hover:bg-white/90'>
                    Following
                </button>
            </div>

            <div className='mt-3 flex items-center gap-1'>
                <span className='text-[19px] font-extrabold leading-tight'>{X_PROFILE.name}</span>
                <VerifiedBadge className='size-[18px]' />
            </div>
            <p className='text-[14px] text-white/55'>{X_PROFILE.handle}</p>

            <p className='mt-3 text-[14.5px] leading-snug'>{X_PROFILE.bio}</p>

            <div className='mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[13.5px] text-white/55'>
                <span className='flex items-center gap-1'>
                    <MapPin className='size-3.5' />
                    {X_PROFILE.location}
                </span>
                <span className='flex items-center gap-1'>
                    <Calendar className='size-3.5' />
                    {X_PROFILE.joined}
                </span>
            </div>

            <div className='mt-3 flex gap-5 text-[13.5px]'>
                <span>
                    <strong className='text-white'>{X_PROFILE.following}</strong> <span className='text-white/55'>Following</span>
                </span>
                <span>
                    <strong className='text-white'>{X_PROFILE.followers}</strong> <span className='text-white/55'>Followers</span>
                </span>
            </div>
        </div>

        <div className='mt-3 flex border-b border-white/10'>
            {['Posts', 'Replies', 'Highlights', 'Media', 'Likes'].map((tab, i) => (
                <button
                    key={tab}
                    type='button'
                    className={cn(
                        'relative flex-1 py-3 text-center text-[14px] transition-colors',
                        i === 0 ? 'font-bold text-white' : 'text-white/55 hover:bg-white/[0.04]'
                    )}
                >
                    {tab}
                    {i === 0 && <span className='absolute inset-x-1/2 bottom-0 h-[3.5px] w-12 -translate-x-1/2 rounded-full bg-[#1d9bf0]' />}
                </button>
            ))}
        </div>

        <article className='border-b border-white/10 px-4 py-3'>
            <div className='mb-1.5 flex items-center gap-1.5 text-[12.5px] text-white/55'>
                <Pin className='size-3.5' />
                Pinned
            </div>
            <div className='flex gap-3'>
                <img src={X_PROFILE.avatar} alt={X_PROFILE.name} className='size-10 shrink-0 rounded-full object-cover' draggable={false} />
                <div className='min-w-0 flex-1'>
                    <div className='flex items-center gap-1 text-[14.5px]'>
                        <span className='font-bold'>{X_PROFILE.name}</span>
                        <VerifiedBadge className='size-[16px]' />
                        <span className='text-white/55'>{X_PROFILE.handle}</span>
                        <span className='text-white/55'>·</span>
                        <span className='text-white/55'>2h</span>
                    </div>
                    <p className='mt-1 whitespace-pre-wrap text-[15.5px] leading-snug'>
                        <a href={X_PROFILE.tweetLink} target='_blank' rel='noreferrer' className='text-[#1d9bf0] hover:underline'>
                            {X_PROFILE.tweetLink}
                        </a>
                        {X_PROFILE.tweetSuffix}
                    </p>
                    <div className='mt-3 grid grid-cols-6 gap-1 text-[12.5px] text-white/55'>
                        {tweetMetrics.map((m, i) => (
                            <span key={i} className='flex items-center gap-1 rounded-full p-1'>
                                <img
                                    src={m.src}
                                    alt=''
                                    draggable={false}
                                    className='size-[17px] object-contain'
                                    style={{ filter: X_ACTION_FILTER }}
                                />
                                {m.label}
                            </span>
                        ))}
                    </div>
                </div>
            </div>
        </article>

        <p className='px-4 py-6 text-center text-[13px] text-white/35'>You&apos;re all caught up</p>
    </div>
)

/* ---------------------------------- Compose window --------------------------------- */

const composeToolbarIcons = [CornerUpLeft, Smile, Check, ImageIcon, Paperclip, PlusCircle]

const ComposeRow: FC<{ label: string; children: ReactNode }> = ({ label, children }): ReactNode => (
    <div className='flex items-center gap-3 border-b border-black/[0.07] px-4 py-2'>
        <span className='w-[60px] shrink-0 text-[12.5px] text-black/55'>{label}</span>
        <div className='min-w-0 flex-1'>{children}</div>
    </div>
)

const ComposeWindow: FC<Omit<FrameProps, 'children' | 'title'> & { ctx: WindowCtx }> = (props): ReactNode => {
    const { win, ctx } = props
    const [subject, setSubject] = useState('')
    const [from, setFrom] = useState('')
    const [body, setBody] = useState('')
    const [website, setWebsite] = useState('')
    const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle')

    const valid = subject.trim() && from.trim() && body.trim()
    const recipient = DESIGNER.email

    const handleSend = (): void => {
        if (!valid || status === 'sending' || status === 'success') return

        if (website.trim()) {
            setStatus('success')
            window.setTimeout(() => ctx.closeWindow(win.uid), 1100)
            return
        }

        setStatus('sending')
        const mailto = `mailto:${recipient}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(`${body}\n\nFrom: ${from}`)}`

        try {
            window.location.href = mailto
            window.setTimeout(() => {
                setStatus('success')
                window.setTimeout(() => ctx.closeWindow(win.uid), 1100)
            }, 350)
        } catch {
            setStatus('error')
        }
    }

    const sendBg =
        status === 'success' ? 'bg-[#28c840]' : !valid || status === 'sending' ? 'bg-[#a9d3ff]' : 'bg-[#0a84ff]'

    const headerCenter = (
        <div className='hidden items-center gap-2 text-black/60 sm:flex'>
            <span className='rounded-full border border-black/[0.07] bg-white px-2 py-1 text-[12px] font-semibold shadow-[0_1px_0_rgba(0,0,0,0.04)]'>
                Aa
            </span>
            {composeToolbarIcons.map((Icon, i) => (
                <span
                    key={i}
                    className='flex items-center gap-0.5 rounded-full border border-black/[0.07] bg-white px-2 py-1 shadow-[0_1px_0_rgba(0,0,0,0.04)]'
                >
                    <Icon className='size-4' />
                    {(i === 3 || i === 4 || i === 5) && <ChevronDown className='size-2.5' />}
                </span>
            ))}
        </div>
    )

    const headerRight = (
        <button
            type='button'
            onClick={handleSend}
            disabled={!valid || status === 'sending' || status === 'success'}
            aria-label='Send'
            className={cn(
                'flex size-9 items-center justify-center rounded-full text-white shadow-[0_1px_2px_rgba(0,0,0,0.12)] transition-colors focus-visible:ring-2 focus-visible:ring-[#0a84ff]/45',
                sendBg
            )}
        >
            {status === 'sending' ? (
                <Loader2 className='size-4 animate-spin' />
            ) : status === 'success' ? (
                <Check className='size-[18px]' strokeWidth={3} />
            ) : (
                <ArrowUp className='size-[18px]' strokeWidth={2.4} />
            )}
        </button>
    )

    return (
        <WindowFrame {...props} headerClassName='bg-white' headerCenter={headerCenter} headerRight={headerRight} bodyClassName='bg-white'>
            <div className='flex h-full flex-col'>
                <ComposeRow label='To:'>
                    <span className='inline-flex items-center gap-1 rounded-md border border-[#cfe2ff] bg-[#e5efff] px-2 py-[2px] text-[13px] text-[#1a3a86]'>
                        {recipient}
                        <ChevronDown className='size-3' />
                    </span>
                </ComposeRow>
                <ComposeRow label='Subject:'>
                    <input
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        autoFocus
                        className='w-full bg-transparent text-[13px] text-[#1a1612] outline-none'
                    />
                </ComposeRow>
                <ComposeRow label='From:'>
                    <input
                        value={from}
                        onChange={(e) => setFrom(e.target.value)}
                        placeholder='Your name – you@example.com'
                        className='w-full bg-transparent text-[13px] text-[#1a1612] outline-none placeholder:text-black/30'
                    />
                </ComposeRow>

                <div className='relative min-h-0 flex-1'>
                    <textarea
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        placeholder='Write your message…'
                        className='size-full resize-none bg-transparent p-5 text-[13.5px] leading-relaxed text-[#1a1612] outline-none placeholder:text-black/30'
                    />
                    <input
                        type='text'
                        name='website'
                        tabIndex={-1}
                        autoComplete='off'
                        value={website}
                        onChange={(e) => setWebsite(e.target.value)}
                        className='absolute left-[-9999px] size-0 opacity-0'
                        aria-hidden='true'
                    />
                    {(status === 'success' || status === 'error') && (
                        <div
                            className={cn(
                                'absolute inset-x-3 bottom-3 rounded-md px-3 py-2 text-[12.5px] shadow-[0_4px_12px_-4px_rgba(0,0,0,0.18)]',
                                status === 'success' ? 'bg-[#e6f7ec] text-[#1a6a2c]' : 'bg-[#fbe8e6] text-[#a01616]'
                            )}
                        >
                            {status === 'success' ? "Sent. Thanks — they'll be in touch." : "Couldn't send. Try again."}
                        </div>
                    )}
                </div>
            </div>
        </WindowFrame>
    )
}

/* ---------------------------------- Window dispatcher --------------------------------- */

const titleForKind = (win: OpenWindow): string => {
    switch (win.kind) {
        case 'project':
            return PROJECTS.find((p) => p.id === win.projectId)?.title ?? 'Project'
        case 'notes':
            return 'Notes'
        case 'photos':
            return 'Photos'
        case 'claude':
            return 'Claude'
        case 'finder':
            return win.folderName ?? 'Finder'
        case 'video':
            return win.title
        case 'tweet':
            return `${X_PROFILE.name} (${X_PROFILE.handle}) / X`
        default:
            return win.title
    }
}

interface WindowViewProps {
    win: OpenWindow
    ctx: WindowCtx
    sectionRef: RefObject<HTMLElement | null>
    onClose: (uid: number) => void
    onFocus: (uid: number) => void
    onMove: (uid: number, x: number, y: number) => void
}

export const WindowView: FC<WindowViewProps> = ({ win, ctx, sectionRef, onClose, onFocus, onMove }): ReactNode => {
    const frameProps = { win, isMobile: ctx.isMobile, sectionRef, onClose, onFocus, onMove }

    if (win.kind === 'compose') {
        return <ComposeWindow {...frameProps} ctx={ctx} />
    }

    let body: ReactNode = null
    switch (win.kind) {
        case 'project': {
            const project = PROJECTS.find((p) => p.id === win.projectId)
            body = project ? <ProjectBody project={project} ctx={ctx} /> : null
            break
        }
        case 'notes':
            body = <NotesBody ctx={ctx} />
            break
        case 'photos':
            body = <PhotosBody ctx={ctx} />
            break
        case 'claude':
            body = <ClaudeBody ctx={ctx} />
            break
        case 'finder':
            body = <FinderBody win={win} ctx={ctx} />
            break
        case 'video':
            body = <VideoBody />
            break
        case 'tweet':
            body = <TweetBody />
            break
        default:
            body = null
    }

    const bodyClassName = win.kind === 'video' ? 'bg-black' : win.kind === 'tweet' ? 'bg-black' : undefined

    return (
        <WindowFrame {...frameProps} title={titleForKind(win)} bodyClassName={bodyClassName}>
            {body}
        </WindowFrame>
    )
}

/* ---------------------------------- Photo lightbox --------------------------------- */

export const PhotoLightbox: FC<{ src: string; onClose: () => void }> = ({ src, onClose }): ReactNode => {
    const overlayRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (overlayRef.current) {
            gsap.fromTo(overlayRef.current, { opacity: 0 }, { opacity: 1, duration: 0.25, ease: 'power2.out' })
        }
    }, [])

    return (
        <div
            ref={overlayRef}
            onMouseDown={(e) => {
                if (e.target === e.currentTarget) onClose()
            }}
            className='fixed inset-0 z-[60] flex items-center justify-center bg-black/85 p-8 backdrop-blur-sm'
        >
            {isVideoSrc(src) ? (
                <video
                    src={src}
                    aria-label='Enlarged video'
                    controls
                    autoPlay
                    playsInline
                    className='max-h-full max-w-full rounded-[8px] bg-black shadow-2xl'
                />
            ) : (
                <img src={src} alt='Enlarged view' draggable={false} className='max-h-full max-w-full rounded-[8px] shadow-2xl' />
            )}
            <button
                type='button'
                onClick={onClose}
                aria-label='Close'
                className='absolute right-5 top-5 flex size-9 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-md transition-colors hover:bg-white/25'
            >
                <XIcon className='size-5' />
            </button>
        </div>
    )
}
